/**
 * Settings → Branding (design-reference §4.13).
 *
 * Reads the tenant's branding profiles (GET /v1/branding) and lets an admin edit
 * the product name, primary colour, light/dark default and hostnames, with a LIVE
 * PREVIEW of the top bar + login + a sample card that re-renders from local edits.
 * Apply writes back (PATCH existing / POST new), a new hostname is a new profile
 * (a duplicate is refused as a conflict), and a logo upload validates the asset
 * before POSTing it. Partner- vs tenant-scope is shown from the profile's scope.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { BrandingConfig, ThemeMode } from '../../api/schema';
import { ApiError } from '../../api/errors';
import { useLoadState } from '../../lib/useLoadState';
import { AA_TEXT, applyAccent, clearAccent, readableInk } from '../../theme/accent';
import { Button, ConfirmModal, ErrorState, Skeleton, useToast } from '../../components';
import { useSettingsApi } from './settingsApi';
import './settings.css';

const SWATCHES = ['#1F6FEB', '#5BA648', '#2D2BA6', '#0E9384', '#B54708'] as const;
const DEFAULT_NAME = 'Docsight';
const DEFAULT_PRIMARY = '#1F6FEB';
const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;
const MODES: readonly { value: ThemeMode; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

interface BrandingData {
  profiles: BrandingConfig[];
}

/** A save blocked on a low-contrast accent, awaiting the admin's confirm/cancel. */
type PendingSave = { kind: 'apply' } | { kind: 'addHost'; host: string };

/** A URL-safe slug for deriving a create hostname from the product name. */
function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '') || 'workspace';
}

export function BrandingScreen(): React.JSX.Element {
  const { toast } = useToast();
  const settingsApi = useSettingsApi();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);

  const [name, setName] = useState(DEFAULT_NAME);
  const [chatbotName, setChatbotName] = useState('');
  const [primary, setPrimary] = useState<string>(DEFAULT_PRIMARY);
  const [mode, setMode] = useState<ThemeMode>('system');
  const [hostInput, setHostInput] = useState('');
  const [assetError, setAssetError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchBranding = useCallback(
    async (signal: AbortSignal): Promise<BrandingData> => {
      const page = await settingsApi.branding.list(signal);
      return { profiles: page.items };
    },
    [settingsApi],
  );
  const state = useLoadState(fetchBranding);
  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;

  const profile =
    state.status === 'ready'
      ? (state.data.profiles.find((p) => p.id === selectedId) ?? state.data.profiles[0])
      : undefined;
  const scope = profile?.scope ?? 'tenant';
  const hostnames = useMemo(
    () => (state.status === 'ready' ? state.data.profiles.map((p) => p.hostname) : []),
    [state],
  );

  // Re-seed the edit form whenever the *selected* profile changes — first load,
  // switching hostname chips, or a fresh reload after a save.
  useEffect(() => {
    if (profile === undefined) return;
    setName(profile.product_name);
    setChatbotName(profile.chatbot_name ?? '');
    setPrimary(profile.theme?.primary ?? DEFAULT_PRIMARY);
    setMode(profile.theme?.mode ?? 'system');
  }, [profile]);

  const runApply = useCallback(() => {
    setApplying(true);
    void (async () => {
      try {
        const theme = { primary, mode };
        const trimmedChatbotName = chatbotName.trim();
        if (profile !== undefined) {
          await settingsApi.branding.update(profile.id, {
            product_name: name,
            chatbot_name: trimmedChatbotName === '' ? null : trimmedChatbotName,
            theme,
          });
        } else {
          await settingsApi.branding.create({
            hostname: `${slug(name)}.abcdash.co.uk`,
            scope: 'tenant',
            product_name: name,
            chatbot_name: trimmedChatbotName === '' ? null : trimmedChatbotName,
            theme,
          });
        }
        // Apply through the contrast-checked path: this recomputes `--on-accent`
        // (readable ink) alongside `--accent`, and reports whether the chosen
        // brand colour clears WCAG AA for text drawn on it (§8 #29).
        const result = applyAccent(primary);
        if (result.applied && !result.passesAA) {
          toast(
            `Branding applied — heads up: text on this accent is low contrast (${result.ratio.toFixed(2)}:1, below 4.5:1 AA).`,
            { tone: 'error' },
          );
        } else {
          toast('Branding applied to this workspace — resolved by hostname at runtime');
        }
        reload?.();
      } catch {
        toast('Could not apply branding — try again', { tone: 'error' });
      } finally {
        setApplying(false);
      }
    })();
  }, [profile, name, chatbotName, primary, mode, toast, reload, settingsApi]);

  // A low-contrast accent doesn't block silently or after the fact — it stops
  // the save at the button and asks the admin to confirm or pick another colour.
  const apply = useCallback(() => {
    if (!readableInk(primary).passesAA) {
      setPendingSave({ kind: 'apply' });
      return;
    }
    runApply();
  }, [primary, runApply]);

  const reset = useCallback(() => {
    if (profile !== undefined) {
      setName(profile.product_name);
      setChatbotName(profile.chatbot_name ?? '');
      setPrimary(profile.theme?.primary ?? DEFAULT_PRIMARY);
      setMode(profile.theme?.mode ?? 'system');
    } else {
      setName(DEFAULT_NAME);
      setChatbotName('');
      setPrimary(DEFAULT_PRIMARY);
      setMode('system');
    }
    clearAccent();
    toast('Preview reset to partner defaults');
  }, [profile, toast]);

  const runAddHost = useCallback(
    (host: string) => {
      void (async () => {
        try {
          const trimmedChatbotName = chatbotName.trim();
          const created = await settingsApi.branding.create({
            hostname: host,
            scope: 'tenant',
            product_name: name,
            chatbot_name: trimmedChatbotName === '' ? null : trimmedChatbotName,
            theme: { primary, mode },
          });
          toast('Hostname added — resolved by hostname at runtime');
          setHostInput('');
          setSelectedId(created.id);
          reload?.();
        } catch (error) {
          if (error instanceof ApiError && (error.status === 409 || error.status === 400)) {
            toast('Hostname conflict — that host is already mapped to a branding profile', { tone: 'error' });
          } else {
            toast('Could not add the hostname — try again', { tone: 'error' });
          }
        }
      })();
    },
    [name, chatbotName, primary, mode, toast, reload, settingsApi],
  );

  const addHost = useCallback(() => {
    const host = hostInput.trim().toLowerCase();
    if (host === '') return;
    if (!HOSTNAME_RE.test(host)) {
      toast('That doesn’t look like a valid hostname', { tone: 'error' });
      return;
    }
    if (hostnames.includes(host)) {
      toast('Hostname conflict — that host is already mapped to a branding profile', { tone: 'error' });
      return;
    }
    if (!readableInk(primary).passesAA) {
      setPendingSave({ kind: 'addHost', host });
      return;
    }
    runAddHost(host);
  }, [hostInput, hostnames, primary, runAddHost, toast]);

  const confirmPendingSave = useCallback(() => {
    const pending = pendingSave;
    if (pending === null) return;
    setPendingSave(null);
    if (pending.kind === 'apply') runApply();
    else runAddHost(pending.host);
  }, [pendingSave, runApply, runAddHost]);

  const cancelPendingSave = useCallback(() => {
    setPendingSave(null);
  }, []);

  // Bumped on every successful upload so the preview refetches even when the
  // stored blob name is unchanged (re-uploading the same image type overwrites
  // it in place — `profile.logo_uri` alone wouldn't move).
  const [logoVersion, setLogoVersion] = useState(0);

  const onFile = useCallback(
    (file: File | undefined) => {
      setAssetError(null);
      if (file === undefined) return;
      const ok = ['image/svg+xml', 'image/png', 'image/jpeg'].includes(file.type);
      if (!ok) {
        setAssetError('That file isn’t a supported image — use an SVG or PNG.');
        return;
      }
      if (profile === undefined) {
        setAssetError('Apply branding first, then upload a logo for this profile.');
        return;
      }
      void (async () => {
        try {
          await settingsApi.branding.uploadAsset(profile.id, 'logo', file);
          toast('Logo updated');
          setLogoVersion((v) => v + 1);
          reload?.();
        } catch {
          setAssetError('Upload failed — try again.');
        }
      })();
    },
    [profile, toast, reload, settingsApi],
  );

  // The stored logo, fetched through the authenticated client and shown as an
  // object URL — a `blob://` storage URI isn't something an `<img src>` can
  // load directly, and `GET /v1/branding/{id}/assets/logo` needs a bearer token
  // a plain `<img>` request never carries.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const profileId = profile?.id;
  const logoUri = profile?.logo_uri ?? null;

  useEffect(() => {
    // Reset immediately on every profile switch — otherwise the previous
    // profile's object URL stays on screen for the length of the new fetch,
    // showing the wrong logo against the newly-selected profile.
    setLogoUrl(null);
    if (profileId === undefined || logoUri === null) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await settingsApi.branding.downloadAsset(profileId, 'logo', controller.signal);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setLogoUrl(objectUrl);
      } catch {
        if (!controller.signal.aborted) setLogoUrl(null);
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [profileId, logoUri, logoVersion, settingsApi]);

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Branding">
        <div className="set-two set-two--5-7">
          <Skeleton height={360} />
          <Skeleton height={360} />
        </div>
        <p className="set-loading-caption">Loading branding…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Branding">
        <ErrorState
          title="We couldn't load your branding"
          body="The settings service didn't respond. Try again in a moment."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const initial = (name.trim()[0] ?? 'D').toUpperCase();
  const contrast = readableInk(primary);

  return (
    <section className="set-wrap" aria-label="Branding">
      <div className="set-two set-two--5-7">
        {/* Left: profile ----------------------------------------------------- */}
        <div className="set-card">
          <div className="set-card__head">
            <h2 className="set-card__title">Branding profile</h2>
            <span className="set-badge set-card__spacer">{scope === 'partner' ? 'PARTNER SCOPE' : 'TENANT SCOPE'}</span>
          </div>

          <div className="ds-field">
            <label className="ds-field__label" htmlFor="brand-name">
              Product name
            </label>
            <input
              id="brand-name"
              className="ds-input"
              value={name}
              onChange={(e) => { setName(e.currentTarget.value); }}
            />
          </div>

          <div className="ds-field" style={{ marginTop: 4 }}>
            <label className="ds-field__label" htmlFor="brand-chatbot-name">
              Assistant name
            </label>
            <input
              id="brand-chatbot-name"
              className="ds-input"
              placeholder={`Ask ${name.trim() === '' ? DEFAULT_NAME : name}`}
              value={chatbotName}
              onChange={(e) => { setChatbotName(e.currentTarget.value); }}
            />
            <span className="set-hint">What the chatbot calls itself. Leave blank to use “Ask {name.trim() === '' ? DEFAULT_NAME : name}”.</span>
          </div>

          <div className="ds-field" style={{ marginTop: 4 }}>
            <span className="ds-field__label" id="primary-colour-label">
              Primary colour
            </span>
            <div className="set-swatches" role="group" aria-labelledby="primary-colour-label">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={c.toLowerCase() === primary.toLowerCase() ? 'set-swatch set-swatch--on' : 'set-swatch'}
                  style={{ background: c }}
                  aria-label={`Primary colour ${c}`}
                  aria-pressed={c.toLowerCase() === primary.toLowerCase()}
                  onClick={() => { setPrimary(c); }}
                />
              ))}
            </div>
          </div>

          <fieldset style={{ border: 'none', margin: '4px 0 0', padding: 0 }}>
            <legend className="ds-field__label">Light / dark default</legend>
            <div className="set-seg" role="group" aria-label="Default colour mode">
              {MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={m.value === mode ? 'set-seg__btn set-seg__btn--on' : 'set-seg__btn'}
                  aria-pressed={m.value === mode}
                  onClick={() => { setMode(m.value); }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="ds-field" style={{ marginTop: 4 }}>
            <span className="ds-field__label">Logo</span>
            <button type="button" className="set-dropzone" onClick={() => { fileRef.current?.click(); }}>
              <span className="set-dropzone__chip">drop logo.svg / png here</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/svg+xml,image/png,image/jpeg"
              className="sr-only"
              aria-label="Upload logo"
              onChange={(e) => { onFile(e.currentTarget.files?.[0]); }}
            />
            {assetError !== null ? (
              <span className="ds-field__error" role="alert">
                {assetError}
              </span>
            ) : null}
            {profile?.logo_uri != null ? (
              logoUrl != null ? (
                <img src={logoUrl} alt={`${name} logo`} className="set-logo-preview" />
              ) : (
                <span className="set-hint">Loading logo preview…</span>
              )
            ) : null}
          </div>

          <div className="ds-field" style={{ marginTop: 4 }}>
            <span className="ds-field__label">Hostnames this profile answers on</span>
            {state.data.profiles.length > 0 ? (
              <div className="set-chips" role="group" aria-label="Select a branding profile">
                {state.data.profiles.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className={p.id === profile?.id ? 'set-chip set-chip--on' : 'set-chip'}
                    aria-pressed={p.id === profile?.id}
                    onClick={() => { setSelectedId(p.id); }}
                  >
                    {p.hostname}
                  </button>
                ))}
              </div>
            ) : (
              <span className="set-hint">No hostnames yet.</span>
            )}
            <div className="set-inline" style={{ marginTop: 8 }}>
              <input
                className="ds-input set-mono"
                style={{ flex: 1 }}
                placeholder="another.host.co.uk"
                aria-label="New hostname"
                value={hostInput}
                onChange={(e) => { setHostInput(e.currentTarget.value); }}
              />
              <Button variant="secondary" size="sm" onClick={addHost} disabled={hostInput.trim() === ''}>
                Add
              </Button>
            </div>
          </div>

          <div className="set-actions-row">
            <Button variant="primary" loading={applying} onClick={apply}>
              Apply to workspace
            </Button>
            <Button variant="secondary" onClick={reset}>
              Reset
            </Button>
          </div>
        </div>

        {/* Right: live preview ---------------------------------------------- */}
        <div className="set-col" aria-label="Live preview">
          <span className="set-eyebrow">LIVE PREVIEW — RESOLVED BY HOSTNAME AT RUNTIME</span>

          <div className="set-preview">
            <div className="set-preview__bar">
              {logoUrl != null ? (
                <img src={logoUrl} alt="" className="set-preview__tile set-preview__tile--logo" />
              ) : (
                <span className="set-preview__tile" style={{ background: primary }}>
                  {initial}
                </span>
              )}
              <span className="set-preview__name">{name}</span>
              <span className="set-card__spacer" />
              <span className="set-preview__ph" style={{ width: 80, height: 10 }} aria-hidden="true" />
              <span
                className="set-preview__ph"
                style={{ width: 24, height: 24, borderRadius: '50%' }}
                aria-hidden="true"
              />
            </div>
            <div className="set-preview__body">
              <div className="set-preview__mini">
                <div className="set-label">Total documents</div>
                <div className="set-preview__kpi">41,286</div>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ok)' }}>▲ 8.2%</div>
              </div>
              <div className="set-preview__mini">
                <div className="set-label" style={{ marginBottom: 8 }}>
                  Primary action
                </div>
                <button type="button" className="set-brandbtn" style={{ background: primary }} tabIndex={-1}>
                  Open review queue
                </button>
              </div>
            </div>
          </div>

          <div className="set-preview">
            <div className="set-preview__login">
              <div className="set-preview__logincard">
                {logoUrl != null ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="set-preview__tile set-preview__tile--logo"
                    style={{ width: 34, height: 34, borderRadius: 10, margin: '0 auto 10px' }}
                  />
                ) : (
                  <span
                    className="set-preview__tile"
                    style={{ background: primary, width: 34, height: 34, borderRadius: 10, fontSize: 16, margin: '0 auto 10px' }}
                  >
                    {initial}
                  </span>
                )}
                <div className="set-preview__name">Sign in to {name}</div>
                <button
                  type="button"
                  className="set-brandbtn"
                  style={{ background: primary, marginTop: 12 }}
                  tabIndex={-1}
                >
                  Sign in with SSO
                </button>
                <div style={{ fontSize: 9.5, color: 'var(--t3)', marginTop: 10 }}>
                  Powered by Askelie · an ABC service
                </div>
              </div>
            </div>
          </div>

          <p className="set-card__foot">
            Partner-scope profiles set the defaults; tenant-scope profiles override per hostname. Light &amp; dark
            are derived from the same tokens.
          </p>
        </div>
      </div>

      <ConfirmModal
        open={pendingSave !== null}
        onCancel={cancelPendingSave}
        onConfirm={confirmPendingSave}
        title="Low-contrast accent colour"
        confirmLabel="Save anyway"
      >
        <strong>{primary}</strong> has a contrast ratio of {contrast.ratio.toFixed(2)}:1 against readable text —
        below the {AA_TEXT}:1 minimum for WCAG AA. Text drawn on this accent (buttons, badges) may be hard to
        read.{' '}
        {pendingSave?.kind === 'addHost' ? (
          <>
            This will still add <strong>{pendingSave.host}</strong> with this colour.
          </>
        ) : (
          'You can save anyway, or cancel and pick a different colour.'
        )}
      </ConfirmModal>
    </section>
  );
}
